import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './ticketDetail.css';
import './tickets.css';
import { getTicketById, updateTicket } from './apiTickets';
import BASE_URL from '../../config';

// Validation schema
const ticketValidationSchema = Yup.object().shape({
  title: Yup.string()
    .min(5, 'Title must be at least 5 characters')
    .max(255, 'Title must not exceed 255 characters')
    .required('Title is required'),
  description: Yup.string()
    .min(20, 'Description must be at least 20 characters')
    .required('Description is required'),
  priority: Yup.string()
    .oneOf(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'], 'Invalid priority')
    .required('Priority is required'),
  issue_type: Yup.string()
    .oneOf(['HARDWARE', 'SOFTWARE', 'GENERAL_QUESTION', 'FEATURE_REQUEST', 'OTHER'], 'Invalid issue type')
    .required('Issue type is required'),
  status: Yup.string()
    .oneOf(['OPEN', 'IN_PROGRESS', 'CLOSED'], 'Invalid status'),
});

const EditTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Check if user is admin
  useEffect(() => {
    fetch(`${BASE_URL}/api/user-profile/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setIsAdmin(data.is_admin);
        setCurrentUserId(data.id);
      })
      .catch((err) => {
        console.error('Error fetching user data:', err);
      });
  }, []);

  // Fetch ticket data
  useEffect(() => {
    if (id) {
      loadTicket();
    }
  }, [id]);

  const loadTicket = async () => {
    try {
      setIsLoading(true);
      const ticketData = await getTicketById(id);
      setTicket(ticketData);
    } catch (error) {
      console.error('Error loading ticket:', error);
      if (error.response && error.response.status === 403) {
        toast.error('You do not have permission to edit this ticket');
        navigate('/user-dashboard/tickets');
      } else if (error.response && error.response.status === 404) {
        toast.error('Ticket not found');
        navigate('/user-dashboard/tickets');
      } else {
        toast.error('Failed to load ticket');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/user-dashboard/tickets/${id}`);
  };

  const handleSubmit = async (values, { setErrors }) => {
    // Permission check: customers can only edit their own tickets
    if (!isAdmin && ticket.created_by !== currentUserId) {
      toast.error('You do not have permission to edit this ticket');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare update data based on user role
      let updateData = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        issue_type: values.issue_type,
      };

      // Admins can also update status
      if (isAdmin) {
        updateData.status = values.status;
      }

      const updatedTicket = await updateTicket(id, updateData);
      toast.success('Ticket updated successfully');
      // Navigate back to ticket detail page
      navigate(`/user-dashboard/tickets/${id}`);
    } catch (error) {
      console.error('Error updating ticket:', error);
      setIsSubmitting(false);

      if (error.response && error.response.data) {
        // Handle validation errors from backend
        const backendErrors = error.response.data;
        if (typeof backendErrors === 'object') {
          setErrors(backendErrors);
        } else if (backendErrors.error) {
          toast.error(backendErrors.error);
        } else {
          toast.error('Failed to update ticket');
        }
      } else {
        toast.error('Failed to update ticket. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="ticket-detail-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-container">
        <div className="empty-state">
          <h2 className="empty-state-title">Ticket not found</h2>
          <button className="btn-primary" onClick={() => navigate('/user-dashboard/tickets')}>
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  const initialValues = {
    title: ticket.title || '',
    description: ticket.description || '',
    priority: ticket.priority || 'MEDIUM',
    issue_type: ticket.issue_type || 'OTHER',
    status: ticket.status || 'OPEN',
  };

  return (
    <div className="ticket-detail-container">
      {/* Header */}
      <div className="ticket-detail-header">
        <div className="ticket-detail-title-section">
          <button className="back-button" onClick={handleBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to Ticket
          </button>
          <div className="ticket-detail-id">Edit Ticket #{id}</div>
          <h1 className="ticket-detail-title">Edit Ticket</h1>
        </div>
      </div>

      {/* Form */}
      <div className="detail-section" style={{ maxWidth: '800px' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={ticketValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isValid }) => (
            <Form className="ticket-form">
              {/* Title Field */}
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Title <span className="required">*</span>
                </label>
                <Field
                  id="title"
                  name="title"
                  type="text"
                  placeholder="Brief summary of your issue"
                  className={`form-input ${
                    errors.title && touched.title ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                />
                <ErrorMessage name="title" component="div" className="error-message" />
              </div>

              {/* Description Field */}
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description <span className="required">*</span>
                </label>
                <Field
                  id="description"
                  name="description"
                  as="textarea"
                  placeholder="Detailed description of your issue"
                  rows="8"
                  className={`form-textarea ${
                    errors.description && touched.description ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error-message"
                />
              </div>

              {/* Issue Type Field */}
              <div className="form-group">
                <label htmlFor="issue_type" className="form-label">
                  Issue Type <span className="required">*</span>
                </label>
                <Field
                  id="issue_type"
                  name="issue_type"
                  as="select"
                  className={`form-select ${
                    errors.issue_type && touched.issue_type ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="HARDWARE">Hardware - Physical device issues</option>
                  <option value="SOFTWARE">Software - Application or system issues</option>
                  <option value="GENERAL_QUESTION">General Question - How-to or information</option>
                  <option value="FEATURE_REQUEST">Feature Request - New functionality</option>
                  <option value="OTHER">Other - Miscellaneous</option>
                </Field>
                <ErrorMessage name="issue_type" component="div" className="error-message" />
              </div>

              {/* Priority Field */}
              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority <span className="required">*</span>
                </label>
                <Field
                  id="priority"
                  name="priority"
                  as="select"
                  className={`form-select ${
                    errors.priority && touched.priority ? 'error' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="LOW">Low - General inquiry</option>
                  <option value="MEDIUM">Medium - Standard issue</option>
                  <option value="HIGH">High - Urgent issue</option>
                  <option value="CRITICAL">Critical - System down</option>
                </Field>
                <ErrorMessage name="priority" component="div" className="error-message" />
              </div>

              {/* Status Field (Admin Only) */}
              {isAdmin && (
                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status <span className="required">*</span>
                  </label>
                  <Field
                    id="status"
                    name="status"
                    as="select"
                    className={`form-select ${
                      errors.status && touched.status ? 'error' : ''
                    }`}
                    disabled={isSubmitting}
                  >
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="CLOSED">Closed</option>
                  </Field>
                  <ErrorMessage name="status" component="div" className="error-message" />
                  <p className="form-hint">
                    Admins can change the ticket status
                  </p>
                </div>
              )}

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Info */}
      {!isAdmin && (
        <div className="info-section" style={{ maxWidth: '800px', marginTop: '2rem' }}>
          <h3 className="info-title">What you can edit</h3>
          <ul style={{ color: '#aab7c4', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Title and description of your ticket</li>
            <li>Priority level</li>
            <li>Note: Only admins can change ticket status</li>
            <li>Comments and attachments can be managed on the ticket detail page</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default EditTicket;
